import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './VersionDiff.module.scss';

/* ────────────────────────────────────────────────────────────
 * VersionDiff — side-by-side comparison of two versions of the
 * same record (committed vs. proposed, previous vs. current).
 *
 *   <VersionDiff
 *     title="Delta Engine & RFC Gatekeeper"
 *     subtitle="Stability data · ICH Q1A(R2)"
 *     meta={<><GitCompare size={13} /> Submitted by A. Nakamura</>}
 *     left={{ label: 'Active — v2.3 (committed)' }}
 *     right={{ label: 'Proposed — v2.4 (staging draft)' }}
 *     leftLines={activeLines}
 *     rightLines={proposedLines}
 *   />
 *
 * Three ways to feed it:
 *   leftLines / rightLines — arrays of strings, diffed for you (LCS,
 *                            so unchanged lines stay aligned and each
 *                            removal pairs with its replacement).
 *   sections               — the section-diff payload the compare
 *                            endpoint returns: [{ key, title, status,
 *                            fromIndex, toIndex }]. Already paired by
 *                            the backend; `key` carries the outline
 *                            depth, the indexes the line numbers.
 *   rows                   — pre-built [{ label, left, right, status }],
 *                            for field-by-field comparisons or when the
 *                            pairing is already known. `status` is
 *                            derived from the two values when omitted.
 *
 * Precedence when more than one is supplied: rows, then sections,
 * then leftLines / rightLines.
 * ──────────────────────────────────────────────────────────── */

const TONE_VALUES = ['neutral', 'primary', 'info', 'success', 'warning', 'danger'];
const STATUS_VALUES = ['unchanged', 'added', 'removed', 'changed'];

const STATUS_CLASS = {
  unchanged: styles.cellUnchanged,
  added: styles.cellAdded,
  removed: styles.cellRemoved,
  changed: styles.cellChanged,
};

/* Colour key shown above the diff. Only three tints ever reach a
   cell: a changed row is red on the left and green on the right, so
   "changed" needs no swatch of its own. */
const LEGEND = [
  {
    status: 'removed',
    marker: '−',
    label: 'Removed',
    hint: 'in the base version only',
  },
  {
    status: 'added',
    marker: '+',
    label: 'Added',
    hint: 'new in the compared version',
  },
  {
    status: 'unchanged',
    marker: '',
    label: 'Unchanged',
    hint: 'identical in both versions',
  },
];

/* Status text read by screen readers — the +/− markers are decorative. */
const STATUS_LABEL = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
};

const isEmptyValue = (value) =>
  value === null || value === undefined || value === '';

/* A row that only carries values gets its status inferred. */
const deriveStatus = (row) => {
  if (row.status) return row.status;

  const hasLeft = !isEmptyValue(row.left);
  const hasRight = !isEmptyValue(row.right);

  if (hasLeft && !hasRight) return 'removed';
  if (!hasLeft && hasRight) return 'added';
  if (hasLeft && hasRight && row.left !== row.right) return 'changed';
  return 'unchanged';
};

/* Longest-common-subsequence length table, built bottom-up. */
const buildLcsTable = (a, b) => {
  const table = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0),
  );

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] =
        a[i] === b[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  return table;
};

/* Diff two arrays of lines into aligned rows. Consecutive
   removals and additions are zipped into `changed` rows so a
   line and its replacement sit on the same visual row. */
export const diffLines = (leftLines = [], rightLines = []) => {
  const table = buildLcsTable(leftLines, rightLines);
  const rows = [];

  let removed = [];
  let added = [];

  const flush = () => {
    const pairCount = Math.max(removed.length, added.length);

    for (let k = 0; k < pairCount; k += 1) {
      const hasRemoved = k < removed.length;
      const hasAdded = k < added.length;

      rows.push({
        left: hasRemoved ? removed[k] : null,
        right: hasAdded ? added[k] : null,
        status: hasRemoved && hasAdded ? 'changed' : hasRemoved ? 'removed' : 'added',
      });
    }

    removed = [];
    added = [];
  };

  let i = 0;
  let j = 0;

  while (i < leftLines.length && j < rightLines.length) {
    if (leftLines[i] === rightLines[j]) {
      flush();
      rows.push({ left: leftLines[i], right: rightLines[j], status: 'unchanged' });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      removed.push(leftLines[i]);
      i += 1;
    } else {
      added.push(rightLines[j]);
      j += 1;
    }
  }

  while (i < leftLines.length) {
    removed.push(leftLines[i]);
    i += 1;
  }
  while (j < rightLines.length) {
    added.push(rightLines[j]);
    j += 1;
  }
  flush();

  return rows;
};

/* Section keys are the outline path — "guide > 2. intro > job draft".
   The number of separators is how deep the heading sits. */
const sectionDepth = (key) => (typeof key === 'string' ? key.split('>').length - 1 : 0);

const normalizeSection = ({ key, title, status, fromIndex, toIndex }) => {
  const inLeft = fromIndex !== null && fromIndex !== undefined;
  const inRight = toIndex !== null && toIndex !== undefined;

  return {
    key,
    title,
    depth: sectionDepth(key),
    /* A status the component doesn't render (e.g. "moved") falls back
       to the indexes: present on both sides means it survived. */
    status: STATUS_VALUES.includes(status)
      ? status
      : (inLeft && inRight && 'changed') || (inLeft && 'removed') || 'added',
    /* Backend indexes are 0-based; the gutter counts from 1. */
    leftNo: inLeft ? fromIndex + 1 : null,
    rightNo: inRight ? toIndex + 1 : null,
  };
};

const sameTitle = (a, b) =>
  typeof a.title === 'string' &&
  typeof b.title === 'string' &&
  a.title.trim().toLowerCase() === b.title.trim().toLowerCase();

/* One row from a removed section, its added counterpart, or both —
   each side keeps its own depth and gutter number. Titles are all we
   get here, so a pair that reads the same is reported as unchanged and
   only a genuine rename is tinted. */
const sectionRow = (removedSection, addedSection) => ({
  key: `${removedSection?.key || ''}|${addedSection?.key || ''}`,
  left: removedSection ? removedSection.title : null,
  right: addedSection ? addedSection.title : null,
  status:
    removedSection && addedSection
      ? (sameTitle(removedSection, addedSection) && 'unchanged') || 'changed'
      : (removedSection && 'removed') || 'added',
  leftDepth: removedSection ? removedSection.depth : 0,
  rightDepth: addedSection ? addedSection.depth : 0,
  leftNo: removedSection ? removedSection.leftNo : null,
  rightNo: addedSection ? addedSection.rightNo : null,
});

/* The compare endpoint pairs nothing — it lists the old document's
   dropped sections and then the new document's added ones. Buffer each
   run and zip it so a section and its replacement share a row instead
   of the right column starting where the left one ends. Same-titled
   sections pair first; whatever is left over pairs by position. */
export const diffSections = (sections = []) => {
  const rows = [];

  let removed = [];
  let added = [];

  const flush = () => {
    const spare = [...added];

    const pairs = removed.map((section) => {
      const match = spare.findIndex((candidate) => sameTitle(candidate, section));
      return { removed: section, added: match === -1 ? null : spare.splice(match, 1)[0] };
    });

    pairs.forEach((pair) => {
      if (!pair.added && spare.length) pair.added = spare.shift();
    });
    spare.forEach((section) => pairs.push({ removed: null, added: section }));

    pairs.forEach((pair) => rows.push(sectionRow(pair.removed, pair.added)));

    removed = [];
    added = [];
  };

  sections.forEach((entry) => {
    const section = normalizeSection(entry);

    if (section.status === 'removed') {
      removed.push(section);
    } else if (section.status === 'added') {
      added.push(section);
    } else {
      /* A section present in both documents anchors the alignment. */
      flush();
      rows.push({
        key: section.key,
        left: section.title,
        right: section.title,
        status: section.status,
        leftDepth: section.depth,
        rightDepth: section.depth,
        leftNo: section.leftNo,
        rightNo: section.rightNo,
      });
    }
  });
  flush();

  return rows;
};

/* One value of one row, on one side. `side` drives which
   statuses tint the cell and which marker it gets. */
const DiffCell = ({ side, row, value, lineNo, numbered, showMarkers, sideLabel }) => {
  const isLeft = side === 'left';
  /* Paired sections can sit at different outline depths. */
  const depth = (isLeft ? row.leftDepth : row.rightDepth) || row.depth || 0;
  const tinted = isLeft
    ? row.status === 'removed' || row.status === 'changed'
    : row.status === 'added' || row.status === 'changed';

  if (isEmptyValue(value)) {
    return <div className={`${styles.cell} ${styles.cellBlank}`} aria-hidden="true" />;
  }

  const classNames = [
    styles.cell,
    tinted ? STATUS_CLASS[isLeft ? 'removed' : 'added'] : STATUS_CLASS.unchanged,
    depth ? styles.cellNested : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} style={depth ? { '--vd-depth': depth } : undefined}>
      <span className={styles.cellSide}>{sideLabel}</span>
      {showMarkers && (
        <span className={styles.marker} aria-hidden="true">
          {tinted ? (isLeft ? '−' : '+') : ''}
        </span>
      )}
      {numbered && <span className={styles.lineNo}>{lineNo}.</span>}
      <span className={styles.text}>
        {tinted && (
          <span className={styles.srOnly}>{`${STATUS_LABEL[row.status]}: `}</span>
        )}
        {value}
      </span>
    </div>
  );
};

DiffCell.propTypes = {
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  row: PropTypes.shape({
    status: PropTypes.oneOf(STATUS_VALUES),
    depth: PropTypes.number,
    leftDepth: PropTypes.number,
    rightDepth: PropTypes.number,
  }).isRequired,
  value: PropTypes.node,
  lineNo: PropTypes.number,
  numbered: PropTypes.bool,
  showMarkers: PropTypes.bool,
  sideLabel: PropTypes.node,
};

DiffCell.defaultProps = {
  value: null,
  lineNo: null,
  numbered: false,
  showMarkers: true,
  sideLabel: null,
};

const VersionDiff = ({
  title,
  subtitle,
  icon,
  meta,
  left,
  right,
  rows,
  sections,
  leftLines,
  rightLines,
  mode,
  numbered,
  showMarkers,
  showSummary,
  showLegend,
  emptyLabel,
  footer,
  className,
  ...rest
}) => {
  /* Resolve whichever input the caller used into aligned rows. */
  const diffRows = useMemo(() => {
    const source =
      rows || (sections ? diffSections(sections) : diffLines(leftLines, rightLines));
    return source.map((row) => ({ ...row, status: deriveStatus(row) }));
  }, [rows, sections, leftLines, rightLines]);

  /* Line numbers run per side and skip that side's blanks, so each
     column reads as its own document. A row that brought its own
     numbers (section indexes from the API) keeps them, and the
     running count follows along so the two never diverge. */
  const numberedRows = useMemo(() => {
    let leftNo = 0;
    let rightNo = 0;

    return diffRows.map((row) => {
      if (!isEmptyValue(row.left)) leftNo = Number.isFinite(row.leftNo) ? row.leftNo : leftNo + 1;
      if (!isEmptyValue(row.right)) {
        rightNo = Number.isFinite(row.rightNo) ? row.rightNo : rightNo + 1;
      }
      return {
        row,
        leftNo: isEmptyValue(row.left) ? null : leftNo,
        rightNo: isEmptyValue(row.right) ? null : rightNo,
      };
    });
  }, [diffRows]);

  const summary = useMemo(
    () =>
      diffRows.reduce(
        (acc, row) => ({
          added: acc.added + (row.status === 'added' || row.status === 'changed' ? 1 : 0),
          removed:
            acc.removed + (row.status === 'removed' || row.status === 'changed' ? 1 : 0),
        }),
        { added: 0, removed: 0 },
      ),
    [diffRows],
  );

  const classNames = [styles.diff, className || ''].filter(Boolean).join(' ');
  const hasHeader = Boolean(title || subtitle || meta || showSummary);
  const isUnified = mode === 'unified';

  /* Columns may be passed without a tone — fall back per side. */
  const headClass = (column, fallbackTone) =>
    [styles.colHead, styles[column.tone || fallbackTone]].filter(Boolean).join(' ');

  return (
    <section className={classNames} {...rest}>
      {hasHeader && (
        <header className={styles.header}>
          <div className={styles.headerMain}>
            {icon && <span className={styles.headerIcon}>{icon}</span>}
            {title && <h6 className={styles.title}>{title}</h6>}
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          </div>

          {(meta || showSummary) && (
            <div className={styles.headerMeta}>
              {showSummary && (
                <span className={styles.summary}>
                  <span className={styles.summaryAdded}>{`+${summary.added}`}</span>
                  <span className={styles.summaryRemoved}>{`−${summary.removed}`}</span>
                </span>
              )}
              {meta}
            </div>
          )}
        </header>
      )}

      {showLegend && (
        <div className={styles.legend} role="note" aria-label="Colour key">
          {LEGEND.map((entry) => (
            <span key={entry.status} className={styles.legendItem}>
              <span
                className={`${styles.legendSwatch} ${STATUS_CLASS[entry.status]}`}
                aria-hidden="true"
              >
                {entry.marker}
              </span>
              <b className={styles.legendLabel}>{entry.label}</b>
              <span className={styles.legendHint}>{entry.hint}</span>
            </span>
          ))}
        </div>
      )}

      {diffRows.length === 0 ? (
        <p className={styles.empty}>{emptyLabel}</p>
      ) : (
        <div className={isUnified ? styles.gridUnified : styles.grid}>
          {!isUnified && (
            <div className={styles.headRow}>
              <div className={headClass(left, 'info')}>
                {left.label}
                {left.caption && <span className={styles.colCaption}>{left.caption}</span>}
              </div>
              <div className={headClass(right, 'warning')}>
                {right.label}
                {right.caption && (
                  <span className={styles.colCaption}>{right.caption}</span>
                )}
              </div>
            </div>
          )}

          {numberedRows.map(({ row, leftNo, rightNo }, index) => (
            /* Section rows carry a stable `key`; line-diff rows have no id
               of their own, so index stands in and the whole list is
               re-derived on every change. */
            <div
              className={isUnified ? styles.rowUnified : styles.row}
              // eslint-disable-next-line react/no-array-index-key
              key={row.key || index}
            >
              {row.label && <span className={styles.rowLabel}>{row.label}</span>}

              {(!isUnified || row.status !== 'added') && (
                <DiffCell
                  side="left"
                  row={row}
                  value={row.left}
                  lineNo={leftNo}
                  numbered={numbered}
                  showMarkers={showMarkers}
                  sideLabel={left.label}
                />
              )}

              {(!isUnified || row.status !== 'unchanged') && (
                <DiffCell
                  side="right"
                  row={row}
                  value={row.right}
                  lineNo={rightNo}
                  numbered={numbered}
                  showMarkers={showMarkers}
                  sideLabel={right.label}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {footer && <footer className={styles.footer}>{footer}</footer>}
    </section>
  );
};

const columnShape = PropTypes.shape({
  label: PropTypes.node,
  caption: PropTypes.node,
  tone: PropTypes.oneOf(TONE_VALUES),
});

VersionDiff.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  // Right-hand header slot — author, timestamp, actions.
  meta: PropTypes.node,
  left: columnShape,
  right: columnShape,
  // Pre-paired rows; `status` is derived when omitted.
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node,
      left: PropTypes.node,
      right: PropTypes.node,
      status: PropTypes.oneOf(STATUS_VALUES),
      // Outline depth — indents the cell one step per level. Per-side
      // depths win when the two sides sit at different levels.
      depth: PropTypes.number,
      leftDepth: PropTypes.number,
      rightDepth: PropTypes.number,
      // Explicit gutter numbers; the running count is used when absent.
      leftNo: PropTypes.number,
      rightNo: PropTypes.number,
    }),
  ),
  // Section-diff payload from the compare endpoint; used when
  // `rows` isn't given.
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.node,
      status: PropTypes.string,
      fromIndex: PropTypes.number,
      toIndex: PropTypes.number,
    }),
  ),
  // Raw lines, diffed for you when neither `rows` nor `sections` is given.
  leftLines: PropTypes.arrayOf(PropTypes.string),
  rightLines: PropTypes.arrayOf(PropTypes.string),
  mode: PropTypes.oneOf(['split', 'unified']),
  numbered: PropTypes.bool,
  showMarkers: PropTypes.bool,
  showSummary: PropTypes.bool,
  // Colour key above the diff — what red, green, and untinted mean.
  showLegend: PropTypes.bool,
  emptyLabel: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
};

VersionDiff.defaultProps = {
  title: null,
  subtitle: null,
  icon: null,
  meta: null,
  left: { label: 'Current version', tone: 'info' },
  right: { label: 'Proposed version', tone: 'warning' },
  rows: null,
  sections: null,
  leftLines: [],
  rightLines: [],
  mode: 'split',
  numbered: true,
  showMarkers: true,
  showSummary: false,
  showLegend: false,
  emptyLabel: 'Nothing to compare.',
  footer: null,
  className: '',
};

export default VersionDiff;
